import * as React from 'react';
import moment from 'moment';
import propz from 'propz';
import { History } from 'history';
import { AppUser } from 'Src/views/App/App';
import { SchoolEvent } from 'Src/models/event';
import { EVENT_STATUS } from 'Src/consts/event';
import { getStudentEvents } from 'Src/helpers/service/student/events';
import {
  AwardItem,
  getStudentAwardsCurrentOverview
} from 'Src/helpers/service/student/awards';
import { getEventGeneratedName } from 'Src/helpers/accessor/accessor';
import { Loader } from 'Src/components/Loader/Loader';
import { StudentPageHeader } from '../components/StudentPageHeader';
import '../components/StudentHub.scss';
import './StudentHome.scss';
import { getEventCommunicationsDashboard } from 'Src/helpers/service/eventCommunications';

interface Props {
  user: AppUser;
  history: History;
  countBadges?: {
    bookings?: string;
    availabilityReports?: string;
  };
}

interface State {
  isLoading: boolean;
  upcomingEvents: SchoolEvent[];
  awards: AwardItem[];
  awardTotal: number;
  points: number;
  error: string;
  communications: {unread: number; mentions: number; conversationsWithUnread: any[]};
}

const UPCOMING_STATUSES = [
  EVENT_STATUS.INVITES_SENT,
  EVENT_STATUS.COLLECTING_INVITE_RESPONSE,
  EVENT_STATUS.ACCEPTED
];

function safeCount(value?: string): number {
  const parsed = Number(value || 0);
  return Number.isFinite(parsed) ? parsed : 0;
}

function greeting(): string {
  const hour = new Date().getHours();
  if (hour < 12) return 'Good morning';
  if (hour < 18) return 'Good afternoon';
  return 'Good evening';
}

export class StudentHome extends React.Component<Props, State> {
  state: State = {
    isLoading: true,
    upcomingEvents: [],
    awards: [],
    awardTotal: 0,
    points: 0,
    error: '', communications: {unread: 0, mentions: 0, conversationsWithUnread: []}
  };

  componentDidMount() {
    const now = new Date();
    const rangeEnd = new Date(now);
    rangeEnd.setMonth(rangeEnd.getMonth() + 3);

    Promise.all([
      getStudentEvents(this.props.user, {
        limit: 5,
        skip: 0,
        order: 'startTime ASC',
        where: {
          startTime: { $gte: now, $lt: rangeEnd },
          status: { $in: UPCOMING_STATUSES }
        }
      }),
      getStudentAwardsCurrentOverview(this.props.user, {
        limit: 3,
        skip: 0,
        order: 'awardedAt DESC',
        where: {}
      }), getEventCommunicationsDashboard(this.props.user)
    ])
      .then(([upcomingEvents, awardsOverview, communications]) => this.setState({
        isLoading: false,
        upcomingEvents,
        awards: awardsOverview.items,
        awardTotal: awardsOverview.total,
        points: awardsOverview.points, communications
      }))
      .catch(() => this.setState({
        isLoading: false,
        error: 'We could not load your overview. Please try again.'
      }));
  }

  openEvent = (eventId: string) => {
    this.props.history.push({
      pathname: '/events/event',
      search: `id=${eventId}`,
      state: { prevRoutePath: '/home' }
    });
  };
  openFirstUnreadConversation = () => { const first = this.state.communications.conversationsWithUnread[0]; if (!first || !first.eventId) return this.props.history.push('/calendar'); this.props.history.push({pathname: '/events/event', search: `id=${first.eventId}`, state: {prevRoutePath: '/home', initialTab: 'Communications', conversationId: first.id}}); };

  render() {
    const { user, history, countBadges = {} } = this.props;
    const { isLoading, upcomingEvents, awards, awardTotal, points, error, communications } = this.state;
    if (isLoading) return <Loader />;

    const firstName = String(propz.get(user, ['firstName'], propz.get(user, ['profile', 'firstName'], '')) || '');
    const lastName = String(propz.get(user, ['lastName'], propz.get(user, ['profile', 'lastName'], '')) || '');
    const clubInvites = safeCount(countBadges.bookings);
    const availability = safeCount(countBadges.availabilityReports);
    const attention = clubInvites + availability;
    const eventsToday = upcomingEvents.filter(event => moment(event.startTime).isSame(moment(), 'day')).length;
    const activePermission = propz.get(user, ['activePermission'], {});
    const formName = propz.get(activePermission, ['details', 'form', 'name'], propz.get(activePermission, ['details', 'formName'], ''));
    const houseName = propz.get(activePermission, ['details', 'house', 'name'], propz.get(activePermission, ['details', 'houseName'], ''));
    const schoolName = propz.get(user, ['activeSchool', 'name'], 'Your school');

    return (
      <main className="parentPage studentHubPage studentHome">
        <StudentPageHeader
          title={`${greeting()}${firstName ? `, ${firstName}` : ''}`}
          subtitle="Your fixtures, clubs, requests and achievements in one place."
          chips={[
            { value: attention, label: attention === 1 ? 'action to complete' : 'actions to complete', tone: attention ? 'amber' : 'green' },
            { value: eventsToday, label: eventsToday === 1 ? 'event today' : 'events today', tone: 'blue' },
            { value: points, label: 'house points this year', tone: 'green' }
          ]}
        />

        {error ? <div className="studentHubAlert">{error}</div> : null}

        <div className="studentHome__layout">
          <div className="studentHome__primary">
            {communications.unread > 0 && <section className="studentHubSurface studentHome__section studentHome__messageCard"><div className="studentHome__sectionHeader"><div><h2>Chats</h2><p>{communications.unread} unread · {communications.mentions} mentions</p></div><span>{communications.unread}</span></div><button className="studentHome__messageCta" type="button" onClick={this.openFirstUnreadConversation}><span>Open unread chat</span><i aria-hidden="true">›</i></button></section>}
            <section
              className="studentHubSurface studentHome__section"
              data-help-anchor="student-home-attention"
            >
              <div className="studentHome__sectionHeader">
                <div><h2>Needs your attention</h2><p>Invitations and availability requests waiting for you.</p></div>
                {attention ? <span>{attention}</span> : null}
              </div>
              {attention === 0 ? (
                <div className="studentHome__clear"><b>✓</b><div><strong>You’re all caught up</strong><p>There are no requests waiting for your response.</p></div></div>
              ) : (
                <div className="studentHome__actionList">
                  {clubInvites ? <button type="button" onClick={() => history.push('/clubBookings/inbox')}><i>C</i><span><strong>Club invitations</strong><small>{clubInvites} waiting for your decision</small></span><b>{clubInvites}</b><em>›</em></button> : null}
                  {availability ? <button type="button" onClick={() => history.push('/availabilityReports/outbox')}><i>A</i><span><strong>Availability reports</strong><small>{availability} reports to review</small></span><b>{availability}</b><em>›</em></button> : null}
                </div>
              )}
            </section>

            <section className="studentHubSurface studentHome__section" data-help-anchor="student-home-events">
              <div className="studentHome__sectionHeader">
                <div><h2>Coming up</h2><p>Your next fixtures, clubs and tournament events.</p></div>
                <button type="button" onClick={() => history.push('/calendar')}>View calendar</button>
              </div>
              {upcomingEvents.length ? (
                <div className="studentHome__eventList">
                  {upcomingEvents.map(event => (
                    <button type="button" key={event.id} onClick={() => this.openEvent(event.id)}>
                      <time><strong>{moment(event.startTime).format('DD')}</strong><span>{moment(event.startTime).format('MMM')}</span></time>
                      <span><strong>{getEventGeneratedName(event, { user })}</strong><small>{moment(event.startTime).format('ddd, D MMM · HH:mm')}</small></span>
                      <em>›</em>
                    </button>
                  ))}
                </div>
              ) : <div className="studentHome__empty"><strong>No upcoming events</strong><span>New fixtures and activities will appear here when your school publishes them.</span></div>}
            </section>
          </div>

          <aside className="studentHome__secondary">
            <section className="studentHubSurface studentHome__profile" data-help-anchor="student-home-profile">
              <div className="studentHome__profileAvatar">{`${firstName.charAt(0)}${lastName.charAt(0)}` || 'S'}</div>
              <h2>{`${firstName} ${lastName}`.trim() || 'Student'}</h2>
              <p>{schoolName}</p>
              <div>{formName ? <span><strong>{formName}</strong><small>Form</small></span> : null}{houseName ? <span><strong>{houseName}</strong><small>House</small></span> : null}<span><strong>{points}</strong><small>Points</small></span></div>
            </section>

            <section className="studentHubSurface studentHome__section" data-help-anchor="student-home-awards">
              <div className="studentHome__sectionHeader"><div><h2>Recent awards</h2><p>Your latest recognition.</p></div><button type="button" onClick={() => history.push('/awards/current')}>{awardTotal} total</button></div>
              {awards.length ? <div className="studentHome__awardList">{awards.map(award => <div key={award.id}><i>★</i><span><strong>{award.awardType.name}</strong><small>{moment(award.awardedAt).format('D MMM YYYY')}{award.reason ? ` · ${award.reason}` : ''}</small></span><b>+{award.points || 0}</b></div>)}</div> : <div className="studentHome__empty"><strong>No awards yet</strong><span>Your awards will appear here.</span></div>}
            </section>
          </aside>
        </div>
      </main>
    );
  }
}
